// ---------------------------------------------------------------------------
// AI-007: 72h AI-action undo log
// ---------------------------------------------------------------------------
// Every AI-initiated state change is recorded here with a before/after
// snapshot so it can be reversed within 72h. After the window expires the
// row is preserved (audit trail) but `undoneAt` cannot be set anymore — the
// undo endpoint returns 410 Gone.

import { pgTable, uuid, text, jsonb, timestamp, index } from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

export const UNDO_WINDOW_MS = 72 * 60 * 60 * 1000; // 72h

export const aiActionsLog = pgTable(
  "ai_actions_log",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    actorUserId: uuid("actor_user_id"), // nullable for autonomous agents
    agentKey: text("agent_key"),
    actionType: text("action_type").notNull(), // e.g. "contact.update", "email.delete"
    resourceType: text("resource_type"), // e.g. "contact", "deal"
    resourceId: text("resource_id"),
    beforeSnapshot: jsonb("before_snapshot").$type<Record<string, unknown> | null>(),
    afterSnapshot: jsonb("after_snapshot").$type<Record<string, unknown> | null>(),
    metadata: jsonb("metadata").$type<Record<string, unknown>>().default({}).notNull(),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
    undoneAt: timestamp("undone_at", tz),
    undoneByUserId: uuid("undone_by_user_id"),
  },
  (table) => [
    index("idx_ai_actions_tenant_created").on(table.tenantId, table.createdAt),
    index("idx_ai_actions_action_type").on(table.tenantId, table.actionType),
    index("idx_ai_actions_resource").on(table.tenantId, table.resourceType, table.resourceId),
  ],
);

export type AiActionLogRow = typeof aiActionsLog.$inferSelect;
export type AiActionLogInsert = typeof aiActionsLog.$inferInsert;
